import { useCallback, useId, useState, type CSSProperties } from "react";
import { roomReactionCopy, siteCopy } from "../../content";
import { HOTSPOTS, type HotspotDefinition, type HotspotId } from "../../config/hotspots";
import type { RoomReaction } from "./roomReaction";
import styles from "./ShackScene.module.css";

export type ShackSceneProps = Readonly<{
  onHotspotActivate: (id: HotspotId) => void;
  soundEnabled: boolean;
  onSoundChange: (enabled: boolean) => void;
  activeHotspotId?: HotspotId | null;
  discoveredHotspotIds?: readonly HotspotId[];
  reaction?: RoomReaction | null;
  soundAvailable?: boolean;
  className?: string;
}>;

type HotspotCustomProperties = CSSProperties &
  Readonly<{
    "--desktop-x": string;
    "--desktop-y": string;
    "--desktop-width": string;
    "--desktop-height": string;
    "--mobile-x": string;
    "--mobile-y": string;
    "--mobile-width": string;
    "--mobile-height": string;
  }>;

const copy = siteCopy.scene;

function reactionAnnouncementFor(reaction: RoomReaction): string {
  return roomReactionCopy[reaction].announcement;
}

function hotspotStyle(hotspot: HotspotDefinition): HotspotCustomProperties {
  const { desktop, mobile } = hotspot.position;

  return {
    "--desktop-x": `${desktop.x}%`,
    "--desktop-y": `${desktop.y}%`,
    "--desktop-width": `${desktop.width}%`,
    "--desktop-height": `${desktop.height}%`,
    "--mobile-x": `${mobile.x}%`,
    "--mobile-y": `${mobile.y}%`,
    "--mobile-width": `${mobile.width}%`,
    "--mobile-height": `${mobile.height}%`,
  };
}

/** The illustrated room only shows when the photograph cannot be displayed. */
type PhotoStatus = "loading" | "ready" | "failed";

function RoomArtwork() {
  const [photoStatus, setPhotoStatus] = useState<PhotoStatus>("loading");

  // A cached photograph can finish loading before React attaches onLoad, so the
  // element is inspected once more the moment the ref lands on it.
  const adoptPhoto = useCallback((photo: HTMLImageElement | null) => {
    if (photo?.complete) {
      setPhotoStatus(photo.naturalWidth > 0 ? "ready" : "failed");
    }
  }, []);

  return (
    <div className={styles.artwork} data-photo={photoStatus} aria-hidden="true">
      <div className={styles.illustration}>
        <div className={styles.ceiling} />
        <div className={styles.backWall} />
        <div className={`${styles.beam} ${styles.beamOne}`} />
        <div className={`${styles.beam} ${styles.beamTwo}`} />
        <div className={styles.sideWallLeft} />
        <div className={styles.sideWallRight} />

        <div className={styles.window}>
          <div className={styles.sky}>
            <span className={styles.sun} />
            <span className={`${styles.cloud} ${styles.cloudOne}`} />
            <span className={`${styles.cloud} ${styles.cloudTwo}`} />
          </div>
          <div className={styles.ocean}>
            <span className={`${styles.wave} ${styles.waveOne}`} />
            <span className={`${styles.wave} ${styles.waveTwo}`} />
            <span className={`${styles.wave} ${styles.waveThree}`} />
          </div>
          <span className={styles.windowBarVertical} />
          <span className={styles.windowBarHorizontal} />
        </div>
        <span className={`${styles.curtain} ${styles.curtainLeft}`} />
        <span className={`${styles.curtain} ${styles.curtainRight}`} />

        <div className={styles.chalkboard}>
          <span className={styles.chalkSun}>☼</span>
          <strong>vandaag</strong>
          <span>iets zachts</span>
          <span className={styles.chalkLine} />
        </div>

        <div className={styles.hearth}>
          <div className={styles.chimney} />
          <div className={styles.mantle}>
            <span className={styles.candle} />
            <span className={styles.shell} />
          </div>
          <div className={styles.fireplace}>
            <div className={styles.fireGlow} />
            <span className={`${styles.flame} ${styles.flameOne}`} />
            <span className={`${styles.flame} ${styles.flameTwo}`} />
            <span className={`${styles.log} ${styles.logOne}`} />
            <span className={`${styles.log} ${styles.logTwo}`} />
          </div>
        </div>

        <div className={styles.table}>
          <span className={styles.tableTop} />
          <span className={`${styles.tableLeg} ${styles.tableLegLeft}`} />
          <span className={`${styles.tableLeg} ${styles.tableLegRight}`} />
          <div className={styles.journal}>
            <span className={styles.journalSpine} />
            <span className={styles.journalMark}>✦</span>
          </div>
          <span className={`${styles.card} ${styles.cardOne}`} />
          <span className={`${styles.card} ${styles.cardTwo}`} />
        </div>

        <div className={styles.floor}>
          <span className={styles.floorLight} />
        </div>
        <div className={styles.rug} />
        <div className={`${styles.cushion} ${styles.cushionOne}`} />
        <div className={`${styles.cushion} ${styles.cushionTwo}`} />
        <div className={`${styles.cushion} ${styles.cushionThree}`} />

        <div className={styles.boards}>
          <span className={`${styles.surfboard} ${styles.boardOne}`}>
            <i />
          </span>
          <span className={`${styles.surfboard} ${styles.boardTwo}`}>
            <i />
          </span>
          <span className={`${styles.surfboard} ${styles.boardThree}`}>
            <i />
          </span>
        </div>

        <div className={styles.plant}>
          <span className={`${styles.leaf} ${styles.leafOne}`} />
          <span className={`${styles.leaf} ${styles.leafTwo}`} />
          <span className={`${styles.leaf} ${styles.leafThree}`} />
          <span className={styles.pot} />
        </div>
      </div>

      <img
        className={styles.scenePhoto}
        ref={adoptPhoto}
        src="/images/scene/shack-reference.webp"
        alt=""
        loading="eager"
        decoding="async"
        onLoad={() => setPhotoStatus("ready")}
        onError={() => setPhotoStatus("failed")}
      />
      <div className={`${styles.photoReaction} ${styles.photoSpecialGlow}`} />
      <div className={`${styles.photoReaction} ${styles.photoFireGlow}`} />
      <div className={`${styles.photoReaction} ${styles.photoWaveGlow}`} />
      <div className={styles.lightWash} />
      <div className={styles.vignette} />
    </div>
  );
}

export function ShackScene({
  onHotspotActivate,
  soundEnabled,
  onSoundChange,
  activeHotspotId = null,
  discoveredHotspotIds = [],
  reaction = null,
  soundAvailable = true,
  className,
}: ShackSceneProps) {
  const titleId = useId();
  const descriptionId = useId();
  const discovered = new Set(discoveredHotspotIds);
  const rootClassName = [styles.scene, className].filter(Boolean).join(" ");

  return (
    <section
      className={rootClassName}
      data-reaction={reaction ?? "none"}
      data-sound={soundEnabled ? "on" : "off"}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <header className={styles.sceneHeader}>
        <div>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          <h2 id={titleId}>{copy.title}</h2>
          <p id={descriptionId} className={styles.sceneIntroduction}>
            {copy.description}
          </p>
        </div>
        {soundAvailable ? (
          <button
            className={styles.soundToggle}
            type="button"
            aria-pressed={soundEnabled}
            onClick={() => onSoundChange(!soundEnabled)}
          >
            <span className={styles.soundIcon} aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span>
              <strong>{copy.soundLabel}</strong>
              <small>{soundEnabled ? "aan" : "uit · optioneel"}</small>
            </span>
          </button>
        ) : null}
      </header>

      <div className={styles.sceneFrame}>
        <RoomArtwork />

        <div className={styles.hotspotLayer} role="group" aria-label={copy.hotspotLayerLabel}>
          {HOTSPOTS.map((hotspot, index) => {
            const isDiscovered = discovered.has(hotspot.id);
            const isActive = activeHotspotId === hotspot.id;

            return (
              <button
                key={hotspot.id}
                className={styles.hotspot}
                style={hotspotStyle(hotspot)}
                type="button"
                aria-label={hotspot.label}
                data-hotspot={hotspot.id}
                data-active={isActive ? "true" : "false"}
                data-discovered={isDiscovered ? "true" : "false"}
                onClick={() => onHotspotActivate(hotspot.id)}
              >
                <span className={styles.hotspotMarker} aria-hidden="true">
                  <span>{isDiscovered ? "✓" : index + 1}</span>
                </span>
                <span className={styles.hotspotLabel} aria-hidden="true">
                  {hotspot.shortLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <p className={styles.liveRegion} aria-live="polite" aria-atomic="true">
        {reaction ? reactionAnnouncementFor(reaction) : ""}
      </p>
    </section>
  );
}
